import api from "api";
import FullLoader from "components/loaders/FullLoader";
import firebase from "config/firebase";
import useAuthContext from "contexts/Auth/useAuthContext";
import {signInWithCustomToken} from "firebase/auth";
import {FC, useEffect} from "react";
import {BrowserRouter, Route, Routes} from "react-router-dom";
import AuthRouter from "routes/auth";
import PrivateRouter from "routes/private";
import PublicRouter from "routes/public";
import {OAuthAuthorize, SSO, Logout} from "pages/auth";
import {mountKadi, kadiSignIn, kadiSignOut} from "./kadiScript";

const Root: FC = () => {
  const auth = useAuthContext();

  // Kadi support chat, replacing the old "Need Help?" button. Loads for
  // everyone, signed in or not; identity is handed over separately below.
  // The craig path stays excluded, as the old help script was.
  const kadiEnabled = !window.location.pathname.includes("craig");

  useEffect(() => {
    if (kadiEnabled) mountKadi();
  }, [kadiEnabled]);

  useEffect(() => {
    if (!kadiEnabled) return;
    if (!auth) {
      // undefined = still resolving; only sign out on a real signed-out state.
      if (auth === null) kadiSignOut();
      return;
    }
    let cancelled = false;
    api
      .externalIdToken()
      .then((token) => {
        if (!cancelled && token) kadiSignIn(token);
      })
      .catch(() => {
        // Support chat is non-essential: fail quietly.
      });
    return () => {
      cancelled = true;
    };
  }, [auth, auth?.uid, kadiEnabled]);

  useEffect(() => {
    if (auth === null) {
      if (!window.location.pathname.startsWith("/auth")) {
        const tokenFromUrl = new URLSearchParams(window.location.search).get("token");
        api
          .authCookie(tokenFromUrl ? {cookie: tokenFromUrl} : undefined)
          .then((token) => {
            if (token) {
              return signInWithCustomToken(firebase.auth, token).catch(() => {});
            }
          })
          .catch(() => {
            tokenFromUrl && signInWithCustomToken(firebase.auth, tokenFromUrl).catch(() => {});
          });
      }
    }
  }, [auth]);

  if (auth === undefined) {
    return <FullLoader />;
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route path="auth/oauth" element={<OAuthAuthorize />} />
        <Route path="auth/sso" element={<SSO />} />
        <Route path="auth/logout" element={<Logout />} />
        <Route path="*" element={auth ? <PrivateRouter /> : <PublicRouter />} />
        <Route path="auth/*" element={<AuthRouter />} />
      </Routes>
    </BrowserRouter>
  );
};

export default Root;
